import React, { useCallback, useState } from "react";
import _ from "lodash";

import { useCaching } from "utils/common";

import { IFixedTable } from "components/FixedTable";
import Cell from "components/FixedTable/Cell";
import DeleteCell from "components/FixedTable/DeleteCell";
import EmptyRow from "components/FixedTable/EmptyRow";
import { TableBody, TableRow } from "components/Table";

const Body = ({
  rows: rowProps,
  headers,
  editable,
  showDeleteCell,
  showEmptyRow,
  onUpdated,
}: IFixedTable) => {
  const [rows, setRows] = useCaching(rowProps);
  const [activeCell, setActiveCell] = useState<{
    row: number;
    col: number;
  } | null>();

  const handleChange = useCallback(
    ({
      rowIndex,
      value,
      key,
    }: {
      rowIndex: number;
      value: string;
      key: string;
    }) => {
      const newRowData = { ...rows[rowIndex], [key]: value };
      const updatedRows = [...rows];

      updatedRows.splice(rowIndex, 1, newRowData);
      setRows(updatedRows);
      onUpdated && onUpdated(updatedRows);
    },
    [rows, setRows, onUpdated]
  );

  const handleClear = useCallback(
    ({ rowIndex }: { rowIndex: number }) => {
      const updatedRows = [...rows];
      const clearedRow = {
        key: rows[rowIndex]["key"],
        [headers[0].key]: rows[rowIndex][headers[0].key],
      };

      updatedRows.splice(rowIndex, 1, clearedRow);
      setRows(updatedRows);

      onUpdated && onUpdated(updatedRows);
    },
    [rows, setRows, onUpdated, headers]
  );

  const handleKeyDown = useCallback(
    ({ rowIndex, colIndex }: { rowIndex: number; colIndex: number }) => {
      let newRow = rowIndex;
      let newCol = colIndex + 1;

      if (newCol >= headers.length) {
        newCol = 1;
        newRow += 1;
      }
      setActiveCell({
        row: newRow,
        col: newCol,
      });
    },
    [headers.length]
  );

  const checkIsCurrentlyEdited = useCallback(
    ({ row, col }: { row: number; col: number }) =>
      activeCell?.row === row && activeCell?.col === col,
    [activeCell]
  );

  const manageCellActiveStatus = useCallback(
    ({
      activate,
      row,
      col,
    }: {
      activate: boolean;
      row: number;
      col: number;
    }) => {
      if (activate) {
        setActiveCell({ row, col });
      } else {
        setActiveCell(null);
      }
    },
    []
  );

  return (
    <TableBody data-testid="table-body-section">
      {_.isArray(rows) &&
        rows.map((row, rowIndex) => (
          <TableRow key={rowIndex} data-testid="table-row">
            {headers.map((header, headerIndex) => (
              <React.Fragment key={headerIndex}>
                {header.render ? (
                  header.render(row, header.key)
                ) : (
                  <Cell
                    cellKey={header.key}
                    value={row[header.key]}
                    editable={headerIndex === 0 ? false : editable}
                    onChangeCellValue={({ value, key }) =>
                      handleChange({ rowIndex, value, key })
                    }
                    onKeyDown={() =>
                      handleKeyDown({
                        rowIndex,
                        colIndex: headerIndex,
                      })
                    }
                    data-testid="table-column-cell"
                    textStyle={headerIndex === 0 ? { fontWeight: 700 } : null}
                    isCurrentlyActive={checkIsCurrentlyEdited({
                      row: rowIndex,
                      col: headerIndex,
                    })}
                    manageCellActiveStatus={({
                      activate,
                    }: {
                      activate: boolean;
                    }) =>
                      manageCellActiveStatus({
                        activate,
                        row: rowIndex,
                        col: headerIndex,
                      })
                    }
                  />
                )}
              </React.Fragment>
            ))}
            {showDeleteCell && (
              <DeleteCell onClear={() => handleClear({ rowIndex })} />
            )}
          </TableRow>
        ))}
      {showEmptyRow && _.isEmpty(rows) && <EmptyRow colSpan={headers.length} />}
    </TableBody>
  );
};

export default Body;
